import { KubernetesObject, V1ContainerPort, V1Service, V1ServiceSpec } from "@kubernetes/client-node"
import { K8SResourceType, KV, KVString, PlanContext } from "yam-types"
import { Settings } from "../settings.d"
import { AccessApproachConfig, ServicePort } from "../types.d"
import { GenServiceName } from "./access"
import { getExternalDNSAnnotations } from "./external-dns"

const serviceAliasNameLabel = (labelName = 'service-alias-name', serviceAliasName: string) => ({ [labelName]: serviceAliasName })

export const composeService = (
  plan: PlanContext, appAccess: AccessApproachConfig, genServiceNameFn: GenServiceName,
  servicePort: number, containerPort: V1ContainerPort, settings: Settings, nodePort?: number
): KubernetesObject => {
  const planData = plan.data
  const defaultAnnotations = settings.serviceAnnotations || {}
  const { serviceName, serviceAliasName } = genServiceNameFn('')

  const defaultLabels = {
    ...settings.serviceLabels,
    ...serviceAliasNameLabel(settings.serviceAliasNameLabel, serviceAliasName)
  }
  const service: V1Service = {
    kind: K8SResourceType.Service,
    metadata: {
      name: serviceName,
      labels: Object.assign({ workload: planData.workloadName }, defaultLabels, appAccess.advancedFeatures?.serviceLabels || {}) as KVString,
      annotations: Object.assign(getExternalDNSAnnotations(appAccess, settings, "service"), defaultAnnotations as KVString || {})
    },
    spec: Object.assign({}, {
      type: (nodePort || (appAccess.servicePorts && appAccess.servicePorts.filter(p => p.nodePort).length > 0)) ? "NodePort" : "ClusterIP",
      selector: {
        workload: planData.workloadName
      },
      ports: appAccess.servicePorts || [{
        nodePort,
        port: servicePort,
        name: containerPort.name,
        protocol: containerPort.protocol,
        targetPort: new Object(containerPort.containerPort)
      }],
      externalName: appAccess.advancedFeatures?.externalName,
      externalTrafficPolicy: appAccess.advancedFeatures?.externalTrafficPolicy,
      ipFamilyPolicy: appAccess.advancedFeatures?.ipFamilyPolicy,
      ipFamilies: appAccess.advancedFeatures?.ipFamilies,
      internalTrafficPolicy: appAccess.advancedFeatures?.internalTrafficPolicy,
      externalIPs: appAccess.advancedFeatures?.internalTrafficPolicy,
      sessionAffinity: appAccess.advancedFeatures?.sessionAffinity,
      topologyKeys: appAccess.advancedFeatures?.topologyKeys
    } as V1ServiceSpec, appAccess.advancedFeatures?.serviceSpec) as unknown as V1ServiceSpec
  }
  return service
}

export const composeLoadBalancer = (plan: PlanContext, appAccess: AccessApproachConfig,
  genServiceNameFn: GenServiceName, servicePort: number, containerPort: V1ContainerPort, settings: Settings
): KubernetesObject => {
  const planData = plan.data
  const defaultAnnotations = settings.loadBalancerAnnotations || {}
  const { serviceName, serviceAliasName } = genServiceNameFn('-lb')
  const defaultLabels = {
    ...settings.serviceLabels,
    ...serviceAliasNameLabel(settings.serviceAliasNameLabel, serviceAliasName)
  }
  const ports = JSON.parse(JSON.stringify(appAccess.servicePorts || [{
    port: servicePort,
    name: containerPort.name,
    protocol: containerPort.protocol,
    targetPort: new Object(containerPort.containerPort)
  }])) as ServicePort[]
  ports.forEach(x => { delete x.nodePort })

  const loadBalancer: V1Service = {
    kind: K8SResourceType.Service,
    metadata: {
      name: serviceName,
      labels: Object.assign({ workload: planData.workloadName }, defaultLabels, appAccess.advancedFeatures?.serviceLabels || {}) as KVString,
      annotations: Object.assign({}, defaultAnnotations, appAccess.advancedFeatures?.loadBalancerAnnotations) as KVString
    },
    spec: Object.assign({}, {
      type: "LoadBalancer",
      selector: {
        workload: planData.workloadName
      },
      // load balancer specific fields
      loadBalancerSourceRanges: appAccess.advancedFeatures?.loadBalancerSourceRanges,
      loadBalancerClass: appAccess.advancedFeatures?.loadBalancerClass,
      loadBalancerIP: appAccess.advancedFeatures?.loadBalancerIP,
      allocateLoadBalancerNodePorts: appAccess.advancedFeatures?.allocateLoadBalancerNodePorts,
      healthCheckNodePort: appAccess.advancedFeatures?.healthCheckNodePort,
      ports,
      clusterIP: appAccess.advancedFeatures?.clusterIP,
      clusterIPs: appAccess.advancedFeatures?.clusterIPs,
      externalName: appAccess.advancedFeatures?.externalName,
      // for loadBalancers, external traffic policy should be local in most cases
      externalTrafficPolicy: appAccess.advancedFeatures?.externalTrafficPolicy || 'Local',
      ipFamilyPolicy: appAccess.advancedFeatures?.ipFamilyPolicy,
      ipFamilies: appAccess.advancedFeatures?.ipFamilies,
      internalTrafficPolicy: appAccess.advancedFeatures?.internalTrafficPolicy,
      externalIPs: appAccess.advancedFeatures?.internalTrafficPolicy,
      sessionAffinity: appAccess.advancedFeatures?.sessionAffinity,
      topologyKeys: appAccess.advancedFeatures?.topologyKeys
    } as V1ServiceSpec, appAccess.advancedFeatures?.loadBalancerSpec) as unknown as V1ServiceSpec
  }
  return loadBalancer
}

export const composeHeadlessSvc = (plan: PlanContext, appAccess: AccessApproachConfig,
  genServiceNameFn: GenServiceName, servicePort: number, containerPort: V1ContainerPort, settings: Settings
): KubernetesObject => {
  const planData = plan.data
  const suffix = '-' + (settings.headlessSuffix || 'headless')
  const { serviceName, serviceAliasName } = genServiceNameFn(suffix)
  const defaultAnnotations = settings.serviceAnnotations || {}
  const defaultLabels = {
    ...settings.serviceLabels,
    ...serviceAliasNameLabel(settings.serviceAliasNameLabel, serviceAliasName)
  }
  const svcName = appAccess.advancedFeatures?.headlessSvcName ?? serviceName

  plan.action(async ctx => {
    const workload = ctx.managedK8SResources.get(plan.data.workloadName)
    if (!workload
      || workload['kind'] !== K8SResourceType.StatefulSet
      || (workload['spec'] as KV)['serviceName']) {
      return
    }
    // Set StatefulSet's ServiceName
    ctx.mergeToYaml({
      name: plan.data.workloadName,
      jsonPath: 'spec',
      defer: true,
      content: { serviceName: svcName }
    })
  }, 'set StatefulSet serviceName')

  const ports = JSON.parse(JSON.stringify(appAccess.servicePorts || [{
    port: servicePort,
    name: containerPort.name,
    protocol: containerPort.protocol,
    targetPort: new Object(containerPort.containerPort)
  }])) as ServicePort[]
  ports.forEach(x => { delete x.nodePort })
  const loadBalancer: V1Service = {
    kind: K8SResourceType.Service,
    metadata: {
      name: svcName,
      labels: Object.assign({ workload: planData.workloadName }, defaultLabels, appAccess.advancedFeatures?.serviceLabels || {}) as KVString,
      annotations: Object.assign({}, defaultAnnotations as KVString)
    },
    spec: Object.assign({}, {
      type: "ClusterIP",
      selector: {
        workload: planData.workloadName
      },
      ports,
      clusterIP: "None",
      publishNotReadyAddresses: appAccess.advancedFeatures?.publishNotReadyAddresses
    } as V1ServiceSpec, appAccess.advancedFeatures?.headlessServiceSpec) as unknown as V1ServiceSpec
  }
  return loadBalancer
}
